import React from 'react';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import {
    FiVideo,
    FiCheckSquare,
    FiMapPin,
    FiMessageSquare,
    FiClock,
    FiShare2
} from 'react-icons/fi';

const cards = [
    {
        id: 'reels-articles',
        icon: FiVideo,
        iconColor: '#2b88d9',
        iconBg: 'rgba(43, 136, 217, 0.08)',
        badge: 'Reels & Articles',
        title: 'Save any reel, video, or article',
        subtitle: 'Skip the 15-minute watch and get straight to the point.',
        preview: {
            type: 'bullets',
            header: 'Summary · 14m YouTube Video',
            items: [
                'The 2-minute friction rule for deep work',
                'Why morning dopamine determines afternoon focus',
                'Recommended book: Deep Work by Cal Newport'
            ]
        }
    },
    {
        id: 'recipes',
        icon: FiCheckSquare,
        iconColor: '#059669',
        iconBg: 'rgba(5, 150, 105, 0.08)',
        badge: 'Recipes',
        title: 'Pull out the recipe',
        subtitle: 'No 3-minute intro story. Just ingredients and steps ready to cook.',
        preview: {
            type: 'checklist',
            header: 'Extracted Recipe · 2 servings',
            items: [
                '200g rigatoni or paccheri',
                '3 egg yolks + 1 whole egg',
                '100g guanciale, cubed',
                '50g Pecorino Romano, freshly grated'
            ]
        }
    },
    {
        id: 'places',
        icon: FiMapPin,
        iconColor: '#e11d48',
        iconBg: 'rgba(225, 29, 72, 0.08)',
        badge: 'Travel & Spots',
        title: 'Pull out cafés and places',
        subtitle: 'Never lose a spot casually mentioned in a travel reel or tweet.',
        preview: {
            type: 'places',
            header: 'Saved Spots · Tokyo Trip',
            items: [
                { name: 'Koffee Mameya', note: 'Omotesando · pour-over flight' },
                { name: 'Fuglen Tokyo', note: 'Shibuya · retro espresso bar' },
                { name: 'Bear Pond Espresso', note: 'Shimokitazawa · signature dirty' }
            ]
        }
    },
    {
        id: 'chat-saves',
        icon: FiMessageSquare,
        iconColor: '#7c3aed',
        iconBg: 'rgba(124, 58, 237, 0.08)',
        badge: 'AI Search',
        title: 'Ask your saves anything',
        subtitle: 'Talk directly with everything you’ve ever saved in natural language.',
        preview: {
            type: 'chat',
            question: 'What can I cook with salmon tonight?',
            answer: 'Based on 2 recipes you saved: 15-min air fryer miso salmon, or lemon butter pan-seared fillet with asparagus.'
        }
    },
    {
        id: 'tasks',
        icon: FiClock,
        iconColor: '#d97706',
        iconBg: 'rgba(217, 119, 6, 0.08)',
        badge: 'Reminders',
        title: 'Turn anything into a task',
        subtitle: 'Auto-detect dates from flyers, tickets, or texts and remind you in time.',
        preview: {
            type: 'reminder',
            header: 'Dentist Confirmation Flyer',
            time: 'Thursday, Oct 12 · 10:30 AM',
            status: 'Reminder scheduled 1h before'
        }
    },
    {
        id: 'capture',
        icon: FiShare2,
        iconColor: '#0891b2',
        iconBg: 'rgba(8, 145, 178, 0.08)',
        badge: 'Universal Capture',
        title: 'Save from anywhere',
        subtitle: 'Share via WhatsApp, Telegram, browser, or camera. No tagging needed.',
        preview: {
            type: 'sources',
            header: 'Auto-synced across channels',
            channels: [
                { name: 'WhatsApp', detail: 'Forwarded voice note (transcribed)' },
                { name: 'Chrome', detail: 'Bookmarklet · 1-click snapshot' },
                { name: 'Telegram', detail: 'Shared screenshot with text OCR' }
            ]
        }
    }
];

const WhatHappensAfterSection = () => {
    const isMobile = useMediaQuery({ maxWidth: 767 });
    return (
        <SectionWrapper className="landing-section">
            <div className="container">
                <HeaderArea>
                    <Eyebrow>What happens after you save</Eyebrow>
                    <MainHeading>
                        Six ways Luckynote puts your saves to work
                    </MainHeading>
                    <SubHeading>
                        You don’t need to organize, tag, or remember where you saved things. Luckynote extracts what matters in the background so it’s ready when you need it.
                    </SubHeading>
                </HeaderArea>

                <Grid $isMobile={isMobile}>
                    {cards.map((card) => {
                        const Icon = card.icon;
                        const { preview } = card;

                        return (
                            <Card key={card.id}>
                                <CardHeader>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <IconWrap $color={card.iconColor} $bg={card.iconBg}>
                                            <Icon size={18} />
                                        </IconWrap>
                                        <Badge>{card.badge}</Badge>
                                    </div>
                                </CardHeader>

                                <CardBody>
                                    <ActionHeading>{card.title}</ActionHeading>
                                    <SubtitleText>{card.subtitle}</SubtitleText>
                                </CardBody>

                                {/* Tactile Artifact / Preview of the actual product result */}
                                <PreviewCard>
                                    <PreviewHeader>
                                        <span className="dot" />
                                        <span>{preview.header || 'Luckynote Extraction'}</span>
                                    </PreviewHeader>

                                    {preview.type === 'bullets' && (
                                        <BulletList>
                                            {preview.items.map((item, idx) => (
                                                <li key={idx}>
                                                    <span className="bullet-dash">—</span>
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </BulletList>
                                    )}

                                    {preview.type === 'checklist' && (
                                        <CheckList>
                                            {preview.items.map((item, idx) => (
                                                <li key={idx}>
                                                    <span className="check-box" />
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </CheckList>
                                    )}

                                    {preview.type === 'places' && (
                                        <PlacesList>
                                            {preview.items.map((place, idx) => (
                                                <li key={idx}>
                                                    <span className="pin">📍</span>
                                                    <div>
                                                        <strong>{place.name}</strong>
                                                        <span className="note">{place.note}</span>
                                                    </div>
                                                </li>
                                            ))}
                                        </PlacesList>
                                    )}

                                    {preview.type === 'chat' && (
                                        <ChatThread>
                                            <ChatBubble $isUser>
                                                <span className="speaker">You</span>
                                                <p>{preview.question}</p>
                                            </ChatBubble>
                                            <ChatBubble>
                                                <span className="speaker">Luckynote</span>
                                                <p>{preview.answer}</p>
                                            </ChatBubble>
                                        </ChatThread>
                                    )}

                                    {preview.type === 'reminder' && (
                                        <ReminderBox>
                                            <div className="time-row">
                                                <span className="clock-icon">⏰</span>
                                                <strong>{preview.time}</strong>
                                            </div>
                                            <div className="status-pill">{preview.status}</div>
                                        </ReminderBox>
                                    )}

                                    {preview.type === 'sources' && (
                                        <SourcesList>
                                            {preview.channels.map((chan, idx) => (
                                                <li key={idx}>
                                                    <span className="channel-tag">{chan.name}</span>
                                                    <span className="detail">{chan.detail}</span>
                                                </li>
                                            ))}
                                        </SourcesList>
                                    )}
                                </PreviewCard>
                            </Card>
                        );
                    })}
                </Grid>

            </div>
        </SectionWrapper>
    );
};

const SectionWrapper = styled.section`
    background-color: #faf8f5;
    padding: 100px 0;

    @media (max-width: 768px) {
        padding: 60px 0;
    }
`;

const HeaderArea = styled.div`
    max-width: 740px;
    margin: 0 auto 56px;
    text-align: center;
`;

const Eyebrow = styled.p`
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #8c827a;
`;

const MainHeading = styled.h2`
    margin: 0 0 16px;
    font-size: 38px;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: #1a1a1a;

    @media (max-width: 768px) {
        font-size: 28px;
    }
`;

const SubHeading = styled.p`
    margin: 0 auto;
    font-size: 17px;
    line-height: 1.6;
    color: #5f6368;
    max-width: 600px;

    @media (max-width: 768px) {
        font-size: 15px;
    }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: ${(props) => (props.$isMobile ? '1fr' : 'repeat(3, 1fr)')};
    gap: 24px;
    margin-bottom: 48px;

    @media (min-width: 768px) and (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const Card = styled.div`
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.07);
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03), 0 12px 24px -10px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    text-align: left;
    transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s cubic-bezier(0.16, 1, 0.3, 1);

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.04), 0 20px 36px -10px rgba(0, 0, 0, 0.08);
        border-color: rgba(0, 0, 0, 0.12);
    }
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const IconWrap = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.$color};
    background: ${(props) => props.$bg};
`;

const Badge = styled.span`
    font-size: 12px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: #71717a;
    background: #f4f4f5;
    padding: 3px 9px;
    border-radius: 6px;
`;

const CardBody = styled.div`
    margin-bottom: 18px;
`;

const ActionHeading = styled.h3`
    margin: 0 0 6px;
    font-size: 18px;
    line-height: 1.35;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #18181b;
`;

const SubtitleText = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: #71717a;
    font-weight: 400;
`;

/* Premium tactile preview card representing actual notes/output */
const PreviewCard = styled.div`
    margin-top: auto;
    background: #fcfbfa;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 13px;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.015);
`;

const PreviewHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #a1a1aa;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);

    .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #10b981;
    }
`;

const BulletList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 7px;

    li {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        line-height: 1.45;
        color: #3f3f46;
        font-size: 12.5px;
    }

    .bullet-dash {
        color: #a1a1aa;
        font-weight: 600;
    }
`;

const CheckList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 7px;

    li {
        display: flex;
        align-items: center;
        gap: 8px;
        line-height: 1.4;
        color: #3f3f46;
        font-size: 12.5px;
    }

    .check-box {
        width: 14px;
        height: 14px;
        border-radius: 3px;
        border: 1.5px solid #d4d4d8;
        background: #ffffff;
        flex-shrink: 0;
    }
`;

const PlacesList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;

    li {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        font-size: 12.5px;
        line-height: 1.35;
    }

    .pin {
        font-size: 13px;
        line-height: 1;
        margin-top: 1px;
    }

    strong {
        color: #18181b;
        font-weight: 600;
        margin-right: 6px;
    }

    .note {
        color: #71717a;
    }
`;

const ChatThread = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ChatBubble = styled.div`
    background: ${(props) => (props.$isUser ? '#ffffff' : '#f4f4f5')};
    border: 1px solid ${(props) => (props.$isUser ? 'rgba(0,0,0,0.06)' : 'transparent')};
    border-radius: 8px;
    padding: 7px 10px;

    .speaker {
        display: block;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: ${(props) => (props.$isUser ? '#71717a' : '#7c3aed')};
        margin-bottom: 2px;
        line-height: 1.2;
    }

    p {
        margin: 0 !important;
        font-size: 12.5px !important;
        line-height: 1.45 !important;
        color: #27272a !important;
    }
`;

const ReminderBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 4px 0;

    .time-row {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        color: #18181b;
    }

    .status-pill {
        display: inline-block;
        font-size: 11px;
        font-weight: 500;
        color: #d97706;
        background: rgba(217, 119, 6, 0.08);
        padding: 3px 8px;
        border-radius: 4px;
        width: fit-content;
    }
`;

const SourcesList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;

    li {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
    }

    .channel-tag {
        font-size: 10.5px;
        font-weight: 600;
        color: #0891b2;
        background: rgba(8, 145, 178, 0.08);
        padding: 2px 6px;
        border-radius: 4px;
        flex-shrink: 0;
    }

    .detail {
        color: #52525b;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export default WhatHappensAfterSection;
